import React from 'react';
import _, { noop, isEmpty, omit } from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Uri from 'urijs';

import { Col } from '@bingads-webui-react/layout';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { withResponsiveSize } from '@bingads-webui-react/with-responsive-size';
import { withPreference } from '@bingads-webui-react/preference-context';
import { StyledLogoWithImage } from '@bingads-webui-react/global-menu-logo';
import { LinkWithFallBack } from '@bingads-webui-react/fallback-feature';
import { ClientCenterCustomerService } from '@bingads-webui/clientcenter-customer-service';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { StyledGlobalSearch } from '@bingads-webui-react/global-search';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { Activity, Scenario } from '@bingads-webui-react/hoc-with-signal';
import { BackToCampaignItem, BackToAccountItem } from '@bingads-webui-react/scope-back-button';

/* Contains everything from the original global-menu component except StyledAccountBreadcrumb
*  has been removed and replaced with a prop AccountBreadcrumb
*  that takes in AccountSelector as a prop.
*
*  The intent is to use StyledAccountBreadcrumbBase (the default prop) to pass in a dummy
*  AccountSelector prop without API calls for CampaignUI to speed up page load times.
*  The GlobalMenu and StyledGlobalMenu components in global-menu.js
*  use StyledAccountBreadcrumb for AccountBreadcrumb, just like before this change.
*
*  Other teams using the StyledGlobalMenu or GlobalMenuWithAccount shared component
*  should observe no change in functionality since it should be like a refactoring to them.
*/

import {
  StyledToolbar,
} from './components';
import { getGlobalMenuStyles } from './styles';
import { StyledAccountBreadcrumbBase } from './components/account-breadcrumb/account-breadcrumb-base';
import { EntityBreadcrumb as DefaultEntityBreadcrumb } from './components/entity-breadcrumb/index';
import ToggleMenuItem from './components/toggle-menu-item';

const GlobalMenuResponsiveSizes = [
  {
    name: 'l',
    minWidth: 0,
  },
  {
    name: 'xl',
    minWidth: 1024,
  },
  {
    name: 'xxl',
    minWidth: 1366,
  },
];

const urlUpdater = ({ url, cid, uid }) =>
  new Uri(url).query('').setQuery('cid', cid).setQuery('uid', uid)
    .toString();

const noCustomerScopeHideOverrides = {
  accountBreadcrumb: true,
  customerSelector: true,
  import: true,
  reports: true,
  searchBar: true,
  tools: true,
  mmc: true,
  myWebsite: true,
};

const adjustHideForScope = ({ currentUser, originalHide }) => ({
  ...originalHide,
  ...(!_.result(currentUser.CurrentCustomer, 'Id') ? noCustomerScopeHideOverrides : {}),
});

export class GlobalMenuBase extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      toolbarExpanded: false,
    };

    this.handleExpandClick = this.handleExpandClick.bind(this);
    this.handleSearchClick = this.handleSearchClick.bind(this);
    if (props.hide && !props.hide.meControl) {
      this.meControlErrorHandler();
    }
  }

  handleExpandClick() {
    this.setState({ toolbarExpanded: true });
  }

  handleSearchClick() {
    this.setState({ toolbarExpanded: false });
  }

  meControlErrorHandler() {
    const {
      i18n,
      serverContext,
    } = this.props;
    if (isEmpty(serverContext.CurrentUser) || isEmpty(serverContext.CurrentUser.Name)) {
      notificationApi.showWarning(i18n.getString(_TL_('Microsoft Advertising problem, try again later.')));
    }
  }

  render() {
    const {
      account,
      AccountBreadcrumb,
      AccountSelector,
      ccCustomerService,
      classes,
      CustomizedToolsBar,
      CustomizedImportBar,
      hide: originalHide,
      i18n,
      NotificationsBar,
      ReportsV4Bar,
      onLogoClick,
      permissions,
      siteMap,
      responsiveSize,
      serverContext,
      settingsContext,
      supportUrl,
      signOutUrl,
      renderSearchResults,
      feedbackProps,
      perfMarker,
      CustomizedTabs,
      CustomerSelector,
      MeControl,
      CustomMenu,
      preferenceService,
      helpProps: originalHelpProps,
      isAccountBreadcrumbReadOnly,
      isSearchBarAsDropdownEnabled,
      onHamburgerClicked,
      isMainNavPanelMode,
      showBackToCampaignButton,
      showEntityBreadcrumb,
      entityBreadcrumbProps,
      EntityBreadcrumb,
      onCopilotClick,
    } = this.props;

    const { toolbarExpanded } = this.state;

    const currentUser =
    {
      ...serverContext.CurrentUser,
      CurrentCustomer: serverContext.CurrentCustomer,
      FullName: serverContext.CurrentUser.Name,
    };

    const ccBaseUrl = settingsContext.Urls.ClientCenterBaseUrl;

    const customerService = new ClientCenterCustomerService({ ccBaseUrl });

    const isMultiUser = !!(currentUser.PersonId && currentUser.PersonId > 0);

    const isFullBreadcrumbMode = responsiveSize === 'xxl';

    const fallbackLinkFlags = {
      viewAllCustomers: false,
      customerManagement: false,
      supportDashboard: false,
      aggregatorWelcome: false,
    };

    const feedbackEnabled = permissions.dynamic.ProductFeedback;

    const isDarkThemeEnabled = permissions.IsDarkThemeEnabled;

    const hide = adjustHideForScope({ currentUser, originalHide });

    const helpProps = {
      ...originalHelpProps,
      showLegacyChat: hide.copilot,
    };

    // Hide merchant center on Pilot 1183
    if (permissions.IsDomesticSimplifiedChineseEnabled && !permissions.IsEnhancedShoppingCampaignAllowed) {
      hide.mmc = true;
    }
    const toolbarHide = omit(hide, 'accountBreadcrumb');

    const logTrace = (message) => {
      if (this.props.globalMenuActivity && this.props.globalMenuActivity.trace) {
        this.props.globalMenuActivity.trace(message);
      }
    };

    // to collect some logs for global menu click, remove later
    const onMenuItemClick = (evt) => {
      const target = evt && evt.nativeEvent && evt.nativeEvent.target;
      if (!target) return;

      const targetClasses = target.classList;
      const parentClasses = target.parentElement && target.parentElement.classList;

      if (targetClasses) {
        if (targetClasses.contains('global-menu-search-dropdown-toggle')) {
          logTrace('# SEARCH global menu clicked');
        } else if (targetClasses.contains('global-menu-import-dropdown-toggle')) {
          logTrace('# IMPORT global menu clicked');
        } else if (targetClasses.contains('global-menu-mmc-dropdown-toggle')) {
          logTrace('# MERCHANT CENTER global menu clicked');
        } else if (targetClasses.contains('global-menu-asset-library-dropdown-toggle')) {
          logTrace('# ASSET LIBRARY global menu clicked');
        } else if (targetClasses.contains('global-menu-tools-dropdown-toggle')) {
          logTrace('# TOOLS global menu clicked');
        } else if (targetClasses.contains('global-menu-help-dropdown-toggle')) {
          logTrace('# HELP global menu clicked');
        } else if (targetClasses.contains('unified-notification-queue-trigger-btn')) {
          logTrace('# NOTIFICATION global menu clicked');
        } else if (parentClasses && parentClasses.contains('global-menu-toolbar-menu-item-callout-clickcreative-studio-asset-library')) {
          logTrace('# ASSET LIBRARY tools menu item clicked');
        } else if (parentClasses && parentClasses.contains('global-menu-toolbar-menu-item-callout-clickmmc-home')) {
          logTrace('# MERCHANT CENTER tools menu item clicked');
        }
      }
    };

    return (
      <div>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */ }
        <div
          className={classNames([
            classes.container,
            'bingads-campaign-react-global-menu',
            `bingads-campaign-react-global-menu-${responsiveSize}`,
            'container-fluid',
          ])}
          onClick={onMenuItemClick}
        >
          {
            _.isFunction(onHamburgerClicked) &&
            <Scenario name="top-nav-hamburger-menu">
              <Activity name="top-nav-hamburger-menu">
                <ToggleMenuItem
                  isMainNavPanelMode={isMainNavPanelMode}
                  onToggle={onHamburgerClicked}
                  i18n={i18n}
                />
              </Activity>
            </Scenario>
          }
          {
            showBackToCampaignButton &&
            <Scenario name="back-to-campaigns">
              <Activity name="back-to-campaigns">
                <BackToCampaignItem
                  i18n={i18n}
                  serverContext={serverContext}
                />
                <BackToAccountItem
                  i18n={i18n}
                  serverContext={serverContext}
                />
              </Activity>
            </Scenario>
          }
          <header className={classes.item} aria-label={i18n.getString(_TL_('Microsoft logo'))} >
            <StyledLogoWithImage i18n={i18n} onLogoClick={onLogoClick} />
          </header>
          <nav
            className={classNames([classes.container, classes.navigationRegionContainer])}
            aria-label={i18n.getString(_TL_('Global menu'))}
          >
            {
              !hide.accountBreadcrumb &&
              (
                <React.Fragment>
                  <Col
                    className={classNames([classes.item, classes.divider])}
                  />
                  <Col className={classes.item}>
                    <AccountBreadcrumb
                      ccCustomerService={ccCustomerService}
                      account={account}
                      responsiveSize={responsiveSize}
                      AccountSelector={AccountSelector}
                      perfMarker={perfMarker}
                      isReadOnly={isAccountBreadcrumbReadOnly}
                    />
                  </Col>
                </React.Fragment>
              )
            }
            {
              showEntityBreadcrumb &&
              (
                <React.Fragment>
                  <Col
                    className={classNames([classes.item, classes.divider])}
                  />
                  <EntityBreadcrumb {...entityBreadcrumbProps} />
                </React.Fragment>
              )
            }
            {
              CustomizedTabs &&
              <Col className={classNames([classes.item, 'customized-tabs'])}>
                <CustomizedTabs />
              </Col>
            }
            <Scenario name="top-nav-persistent-white-box">
              <Activity name="top-nav-persistent-white-box">
                <Col className={classNames([classes.item, classes.stretch])}>
                  {((!toolbarExpanded || responsiveSize !== 'l') && !hide.searchBar && !isSearchBarAsDropdownEnabled) &&
                    <StyledGlobalSearch
                      siteMap={siteMap}
                      renderSearchResults={renderSearchResults}
                      onCopilotClick={onCopilotClick}
                      serverContext={serverContext}
                    />
                  }
                </Col>
              </Activity>
            </Scenario>
            <Scenario name="top-nav-toolbar">
              <Activity name="top-nav-toolbar">
                <Col className={classes.item}>
                  <StyledToolbar
                    i18n={i18n}
                    responsiveSize={responsiveSize}
                    expanded={toolbarExpanded}
                    onExpandClick={() => this.handleExpandClick()}
                    onSearchClick={() => this.handleSearchClick()}
                    NotificationsBar={NotificationsBar}
                    ReportsV4Bar={ReportsV4Bar || null}
                    CustomizedToolsBar={CustomizedToolsBar}
                    CustomizedImportBar={CustomizedImportBar}
                    hide={toolbarHide}
                    feedbackProps={feedbackProps}
                    feedbackEnabled={feedbackEnabled}
                    helpProps={helpProps}
                    siteMap={siteMap}
                    renderSearchResults={renderSearchResults}
                    isSearchBarAsDropdownEnabled={isSearchBarAsDropdownEnabled}
                    serverContext={serverContext}
                    onCopilotClick={onCopilotClick}
                  />
                </Col>
              </Activity>
            </Scenario>
            {
              !hide.meControl &&
              <Col className={classes.item}>
                <MeControl
                  user={currentUser}
                  i18n={i18n}
                  ccBaseUrl={settingsContext.Urls.RootUIUrl}
                  customerService={customerService}
                  url={window.location.href}
                  isMultiUser={isMultiUser}
                  isFullBreadcrumbMode={isFullBreadcrumbMode}
                  customerUrlUpdater={urlUpdater}
                  supportUrl={supportUrl}
                  signOutUrl={signOutUrl}
                  fallbackLinkFlags={fallbackLinkFlags}
                  renderLink={LinkWithFallBack}
                  hideCustomerSelector={hide.customerSelector}
                  hideCustomerManagement={hide.customerManagement}
                  perfMarker={perfMarker}
                  CustomerSelector={CustomerSelector}
                  CustomMenu={CustomMenu}
                  isDarkThemeEnabled={isDarkThemeEnabled}
                  preferenceService={preferenceService}
                />
              </Col>
            }
          </nav>
        </div>
      </div>
    );
  }
}

GlobalMenuBase.propTypes = {
  classes: PropTypes.shape({
    container: PropTypes.string.isRequired,
    item: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  AccountBreadcrumb: PropTypes.elementType,
  AccountSelector: PropTypes.elementType,
  ccCustomerService: PropTypes.func, // for demo page and
  renderSearchResults: PropTypes.arrayOf(PropTypes.func).isRequired,
  account: PropTypes.shape({
    Id: PropTypes.number.isRequired,
    Name: PropTypes.string.isRequired,
    AccountNumber: PropTypes.string.isRequired,
    ParentCustomerId: PropTypes.number.isRequired,
  }).isRequired,
  permissions: PropTypes.shape({}).isRequired,
  siteMap: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
  })).isRequired,
  responsiveSize: PropTypes.string.isRequired,
  settingsContext: PropTypes.shape({}).isRequired,
  serverContext: PropTypes.shape({}).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  supportUrl: PropTypes.string.isRequired,
  signOutUrl: PropTypes.string,
  NotificationsBar: PropTypes.elementType.isRequired,
  ReportsV4Bar: PropTypes.elementType.isRequired,
  CustomizedToolsBar: PropTypes.elementType,
  CustomizedImportBar: PropTypes.elementType,
  hide: PropTypes.shape({
    accountBreadcrumb: PropTypes.bool,
    searchBar: PropTypes.bool,
    customerSelector: PropTypes.bool,
    meControl: PropTypes.bool,
  }).isRequired,
  onLogoClick: PropTypes.func,
  feedbackProps: PropTypes.shape({
    feedbackLogger: PropTypes.shape({
      log: PropTypes.func,
    }),
    hideFeedback: PropTypes.bool,
    isTestEnv: PropTypes.bool,
    email: PropTypes.string,
    pageId: PropTypes.string,
    sessionId: PropTypes.string,
  }),
  helpProps: PropTypes.shape({
    showGetHelp: PropTypes.bool,
    showGetStarted: PropTypes.bool,
    showCommunity: PropTypes.bool,
    customizedMenuItems: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      onClick: PropTypes.func,
      externalLink: PropTypes.string,
      children: PropTypes.array,
      showTag: PropTypes.string,
    })),
  }),
  perfMarker: perfMarkerPropTypes,
  CustomizedTabs: PropTypes.elementType,
  CustomerSelector: PropTypes.elementType,
  MeControl: PropTypes.elementType,
  CustomMenu: PropTypes.elementType,
  isAccountBreadcrumbReadOnly: PropTypes.bool,
  isSearchBarAsDropdownEnabled: PropTypes.bool,
  onHamburgerClicked: PropTypes.func,
  isMainNavPanelMode: PropTypes.bool,
  showBackToCampaignButton: PropTypes.bool,
  showEntityBreadcrumb: PropTypes.bool,
  entityBreadcrumbProps: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  EntityBreadcrumb: PropTypes.elementType,
  onCopilotClick: PropTypes.func,
};

// Mock data for global menu here
GlobalMenuBase.defaultProps = {
  showEntityBreadcrumb: false,
  entityBreadcrumbProps: null,
  EntityBreadcrumb: DefaultEntityBreadcrumb,
  ccCustomerService: noop(),
  CustomizedToolsBar: null,
  CustomizedImportBar: null,
  onLogoClick: noop,
  AccountBreadcrumb: StyledAccountBreadcrumbBase,
  AccountSelector: null,
  feedbackProps: {
    feedbackLogger: { log: () => { } },
    hideFeedback: false,
    isTestEnv: true,
    email: '',
    pageId: '',
    sessionId: '',
  },
  helpProps: {
    showGetHelp: true,
    showGetStarted: true,
    showCommunity: true,
    customizedMenuItems: [],
  },
  perfMarker: null,
  CustomizedTabs: null,
  CustomerSelector: null,
  MeControl: null,
  CustomMenu: null,
  signOutUrl: null,
  isAccountBreadcrumbReadOnly: false,
  isSearchBarAsDropdownEnabled: false,
  onHamburgerClicked: null,
  isMainNavPanelMode: false,
  showBackToCampaignButton: false,
  onCopilotClick: noop,
};

export const StyledGlobalMenuBase = _.compose(
  _.partial(withDefaultStyles, _, getGlobalMenuStyles),
  withI18nConsumer,
  _.partial(withResponsiveSize, _, GlobalMenuResponsiveSizes),
  withPreference,
  withPermissions
)(GlobalMenuBase);
